"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { resolveItemUrl } from "@/lib/directus";
import type { SafeNavigationItem } from "@/lib/directus/typeGuards";
import { cn } from "@/lib/utils";
import { NavMenuDot } from "@/shared/components";
import { CONTENT_FRAME } from "@/shared/components/BlockWrapper";
import {
  hasMenuPromoContent,
  MenuPromoBanner,
  type MenuPromo,
} from "./MenuPromoBanner";

export type { MenuPromo };

function hasMegaMenu(item: SafeNavigationItem): boolean {
  return (item.children ?? []).some((child) => (child.children?.length ?? 0) > 0);
}

interface ServicesMegaMenuProps {
  item: SafeNavigationItem;
  promoBlock?: MenuPromo | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function ServicesMegaMenu({
  item,
  promoBlock,
  onMouseEnter,
  onMouseLeave,
}: ServicesMegaMenuProps) {
  const categories = item.children ?? [];
  const [activeCategoryIdx, setActiveCategoryIdx] = useState(0);

  return (
    <div
      className="fixed left-0 right-0 top-[var(--header-height)] z-40 border-b border-border bg-background"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={`${CONTENT_FRAME} flex items-stretch`}>
        <div className="flex w-[480px] shrink-0 flex-col border-r border-border py-10">
          {categories.map((category, index) => {
            const categoryUrl = resolveItemUrl(category);
            const isActive = index === activeCategoryIdx;
            return (
              <Link
                key={category.id}
                href={categoryUrl}
                className={cn(
                  "flex items-center gap-4 px-10 py-4 text-link transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-secondary hover:bg-muted"
                )}
                onMouseEnter={() => setActiveCategoryIdx(index)}
              >
                <span className="flex-1">{category.title}</span>
                <ChevronRight
                  className={cn(
                    "size-6 shrink-0",
                    isActive ? "text-primary-foreground" : "text-secondary"
                  )}
                  aria-hidden
                />
              </Link>
            );
          })}
        </div>

        <div className="grid min-h-0 flex-1 border-r border-border">
          {categories.map((category, index) => {
            const categoryServices = category.children ?? [];
            const isActive = index === activeCategoryIdx;

            return (
              <div
                key={category.id}
                className={cn(
                  "col-start-1 row-start-1 flex flex-col py-10",
                  isActive ? "z-10" : "invisible pointer-events-none"
                )}
                aria-hidden={!isActive}
              >
                {categoryServices.map((service) => {
                  const serviceUrl = resolveItemUrl(service);
                  return (
                    <Link
                      key={service.id}
                      href={serviceUrl}
                      className="px-10 py-4 text-body text-secondary transition-colors hover:text-primary"
                      tabIndex={isActive ? 0 : -1}
                    >
                      {service.title}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </div>

        {hasMenuPromoContent(promoBlock) && (
          <MenuPromoBanner promo={promoBlock} />
        )}
      </div>
    </div>
  );
}

interface SimpleDropdownProps {
  item: SafeNavigationItem;
  leftOffset: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function SimpleDropdown({
  item,
  leftOffset,
  onMouseEnter,
  onMouseLeave,
}: SimpleDropdownProps) {
  const children = item.children ?? [];

  return (
    <div
      className="fixed left-0 right-0 top-[var(--header-height)] z-40 border-b border-border bg-background"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex w-full items-stretch">
        <div
          className="shrink-0 border-r border-border"
          style={{ width: leftOffset }}
          aria-hidden
        />
        <div className="flex w-[360px] shrink-0 flex-col border-r border-border py-10">
          {children.map((child) => {
            const url = resolveItemUrl(child);
            return (
              <Link
                key={child.id}
                href={url}
                className="px-10 py-4 text-body text-secondary transition-colors hover:text-primary"
              >
                {child.title}
              </Link>
            );
          })}
        </div>
        <div className="min-h-0 flex-1" aria-hidden />
      </div>
    </div>
  );
}

export interface DesktopNavProps {
  items: SafeNavigationItem[];
  menuPromo?: MenuPromo | null;
  searchOpen?: boolean;
  onMenuOpenChange?: (isOpen: boolean) => void;
}

export function DesktopNav({
  items,
  menuPromo,
  searchOpen = false,
  onMenuOpenChange,
}: DesktopNavProps) {
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [prevSearchOpen, setPrevSearchOpen] = useState(searchOpen);
  const [dropdownLeftOffset, setDropdownLeftOffset] = useState(0);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (searchOpen !== prevSearchOpen) {
    setPrevSearchOpen(searchOpen);
    if (searchOpen) {
      setOpenItemId(null);
      onMenuOpenChange?.(false);
    }
  }

  const openMenu = useCallback(
    (id: string, leftOffset?: number) => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      onMenuOpenChange?.(true);
      setOpenItemId(id);
      if (leftOffset !== undefined) {
        setDropdownLeftOffset(leftOffset);
      }
    },
    [onMenuOpenChange]
  );

  const scheduleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setOpenItemId(null);
      onMenuOpenChange?.(false);
    }, 150);
  }, [onMenuOpenChange]);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(() => {
    document.body.style.overflow = openItemId ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [openItemId]);

  return (
    <>
      {openItemId !== null && (
        <div
          className="fixed inset-x-0 bottom-0 top-[var(--header-height)] z-30 bg-secondary/50"
          aria-hidden
          onClick={() => {
            setOpenItemId(null);
            onMenuOpenChange?.(false);
          }}
        />
      )}
      <nav className="relative z-40 flex items-center gap-12">
      {items.map((item) => {
        const children = item.children ?? [];
        const hasChildren = children.length > 0;
        const isMega = hasMegaMenu(item);
        const isOpen = openItemId === item.id;
        const url = item.type !== "group" ? resolveItemUrl(item) : null;

        if (!hasChildren) {
          return (
            <Link
              key={item.id}
              href={url || "#"}
              className="header-nav-item flex items-center gap-2 whitespace-nowrap font-sans text-base font-normal uppercase leading-normal text-secondary transition-colors hover:text-primary"
            >
              <NavMenuDot />
              {item.title}
            </Link>
          );
        }

        return (
          <div
            key={item.id}
            className="relative"
            onMouseEnter={(event) => {
              if (isMega) {
                openMenu(item.id);
                return;
              }
              openMenu(item.id, event.currentTarget.getBoundingClientRect().left);
            }}
            onMouseLeave={scheduleClose}
          >
            <button
              type="button"
              aria-expanded={isOpen}
              className={cn(
                "header-nav-item flex items-center gap-2 whitespace-nowrap font-sans text-base font-normal uppercase leading-normal transition-colors",
                isOpen ? "text-primary" : "text-secondary hover:text-primary"
              )}
            >
              <NavMenuDot active={isOpen} />
              {url ? (
                <Link href={url} className="text-inherit uppercase">
                  {item.title}
                </Link>
              ) : (
                item.title
              )}
            </button>

            {isOpen && isMega && (
              <ServicesMegaMenu
                item={item}
                promoBlock={menuPromo}
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
              />
            )}
            {isOpen && !isMega && (
              <SimpleDropdown
                item={item}
                leftOffset={dropdownLeftOffset}
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
              />
            )}
          </div>
        );
      })}
      </nav>
    </>
  );
}
